/**
 * useLocationsFilter - Hook for filtering public locations
 *
 * Search by name/address/city; filter by city
 */

import { useMemo, useState, useCallback } from "react";
import { useDebounce } from "@/hooks";
import type { GetLocationResponse } from "@/openapi/generated";
import { filterPublicLocations } from "./filterPublicLocations";

export interface LocationsFilterInitialState {
  search?: string;
  cityFilter?: string | null;
}

export { filterPublicLocations } from "./filterPublicLocations";
export type { PublicLocationsFilterInput } from "./filterPublicLocations";

export const useLocationsFilter = (
  locations: GetLocationResponse[] | undefined,
  initial?: LocationsFilterInitialState,
) => {
  const [search, setSearch] = useState(initial?.search ?? "");
  const debouncedSearch = useDebounce(search, 300);
  const [cityFilter, setCityFilter] = useState<string | null>(
    initial?.cityFilter ?? null,
  );

  const filteredData = useMemo(
    () =>
      filterPublicLocations(locations, {
        search: debouncedSearch,
        city: cityFilter,
      }),
    [locations, debouncedSearch, cityFilter],
  );

  const hasActiveFilters = useMemo(() => cityFilter !== null, [cityFilter]);

  const resetFilters = useCallback(() => {
    setCityFilter(null);
  }, []);

  return {
    search,
    setSearch,
    cityFilter,
    setCityFilter,
    filteredData,
    hasActiveFilters,
    isSearchPending: search !== debouncedSearch,
    resetFilters,
  };
};
